import React from 'react';
import { Button, Card, Col, Row, Typography, Space, Tag, Divider, Badge } from 'antd';
import {
    RobotOutlined,
    UsergroupAddOutlined,
    QuestionCircleOutlined,
    BranchesOutlined,
    ThunderboltOutlined,
    RocketOutlined,
    DatabaseOutlined,
    ApiOutlined,
    AppstoreAddOutlined,
    HistoryOutlined,
    NodeIndexOutlined
} from '@ant-design/icons';


const { Title, Paragraph, Text } = Typography;

export default function EmptyStateView({ onCreateNew }) {
    const features = [
        {
            icon: <QuestionCircleOutlined style={{ fontSize: '24px', color: '#1890ff' }} />,
            title: 'Collect Data',
            description: 'Ask questions to collect Name, Email, Phone, Address, and more.'
        },
        {
            icon: <UsergroupAddOutlined style={{ fontSize: '24px', color: '#52c41a' }} />,
            title: 'Follower Checks',
            description: 'Create exclusive experiences by checking if a user follows you.'
        },
        {
            icon: <BranchesOutlined style={{ fontSize: '24px', color: '#722ed1' }} />,
            title: 'Multi-step Flows',
            description: 'Build interactive, conversational journeys with multiple steps.'
        },
        {
            icon: <RobotOutlined style={{ fontSize: '24px', color: '#fa8c16' }} />,
            title: 'Smart Filtering',
            description: 'Tag and organize contacts based on their responses automatically.'
        },
        {
            icon: <DatabaseOutlined style={{ fontSize: '24px', color: '#fa541c' }} />,
            title: 'Insights & Export',
            description: 'View specific contacts, meaningful insights and download collected information.'
        },
        {
            icon: <HistoryOutlined style={{ fontSize: '24px', color: '#eb2f96' }} />,
            title: 'Follow-up Messages',
            description: 'Re-engage users with automated follow-ups after a set delay.'
        },
        {
            icon: <NodeIndexOutlined style={{ fontSize: '24px', color: '#13c2c2' }} />,
            title: 'Connect Flows',
            description: 'Seamlessly trigger another flow from within a flow for modular automation.'
        }
    ];


    const comingSoon = [
        "Google Sheet Integration",
        "Make.com Integration",
        "Custom Webhooks"
    ];

    const useCases = [
        "Lead Generation",
        "Customer Support",
        "Feedback & Surveys",
        "Contests & Giveaways"
    ];

    return (
        <div style={{
            height: '100%',
            overflowY: 'auto', // Enable scrolling
            background: '#f8fafc',
            borderRadius: '16px',
            padding: '20px', // Reduced padding slightly for mobile
            display: 'flex',
            flexDirection: 'column',
        }}>
            <div style={{
                maxWidth: '800px',
                margin: 'auto', // Centers vertically and horizontally if space permits, top-aligns if overflows
                width: '100%',
                textAlign: 'center',
                padding: '20px 0' // Inner padding
            }}>
                <RocketOutlined style={{ fontSize: '48px', color: '#1890ff', marginBottom: '24px' }} />

                <Title level={2} style={{ marginBottom: '16px' }}>
                    Supercharge Your Comment to DMs (Both Organic and Ads) with Flow Automation
                </Title>

                <Paragraph style={{ fontSize: '16px', color: '#64748b', marginBottom: '40px', maxWidth: '600px', margin: '0 auto 40px' }}>
                    Go beyond simple auto-replies. Build powerful, interactive experiences that capture leads, qualified customers, and automate your business 24/7.
                </Paragraph>

                <Row gutter={[24, 24]} style={{ marginBottom: '48px' }}>
                    {features.map((feature, index) => (
                        <Col xs={24} sm={12} key={index}>
                            <Card
                                bordered={false}
                                style={{
                                    height: '100%',
                                    textAlign: 'left',
                                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)'
                                }}
                            >
                                <Space align="start">
                                    <div style={{
                                        padding: '12px',
                                        background: '#f0f9ff',
                                        borderRadius: '12px',
                                        marginRight: '8px'
                                    }}>
                                        {feature.icon}
                                    </div>
                                    <div>
                                        <Text strong style={{ fontSize: '16px', display: 'block', marginBottom: '4px' }}>
                                            {feature.title}
                                        </Text>
                                        <Text type="secondary" style={{ fontSize: '14px' }}>
                                            {feature.description}
                                        </Text>
                                    </div>
                                </Space>
                            </Card>
                        </Col>
                    ))}
                    <Col xs={24} sm={12}>
                        <Card
                            bordered={false}
                            style={{
                                height: '100%',
                                textAlign: 'left',
                                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
                                background: '#fafafa',
                                border: '1px dashed #d9d9d9'
                            }}
                        >
                            <Space align="start">
                                <div style={{
                                    padding: '12px',
                                    background: '#f5f5f5',
                                    borderRadius: '12px',
                                    marginRight: '8px'
                                }}>
                                    <AppstoreAddOutlined style={{ fontSize: '24px', color: '#8c8c8c' }} />
                                </div>
                                <div>
                                    <Text strong style={{ fontSize: '16px', display: 'block', marginBottom: '4px', color: '#8c8c8c' }}>
                                        Coming Soon...
                                    </Text>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '8px' }}>
                                        {comingSoon.map((item, i) => (
                                            <Tag key={i} color="default" style={{ margin: 0 }}>{item}</Tag>
                                        ))}
                                    </div>
                                </div>
                            </Space>
                        </Card>
                    </Col>
                </Row>


                <div style={{ marginBottom: '40px' }}>
                    <Text strong style={{ display: 'block', marginBottom: '16px', color: '#64748b', textTransform: 'uppercase', fontSize: '12px', letterSpacing: '1px' }}>
                        Perfect for
                    </Text>
                    <Space size="large" wrap justify="center">
                        {useCases.map((useCase, index) => (
                            <Tag
                                key={index}
                                style={{
                                    padding: '6px 16px',
                                    borderRadius: '20px',
                                    border: '1px solid #e2e8f0',
                                    background: '#fff',
                                    fontSize: '14px',
                                    color: '#475569'
                                }}
                            >
                                {useCase}
                            </Tag>
                        ))}
                    </Space>
                </div>

                <Button
                    type="primary"
                    size="large"
                    icon={<ThunderboltOutlined />}
                    onClick={onCreateNew}
                    style={{
                        height: '48px',
                        padding: '0 32px',
                        fontSize: '16px',
                        borderRadius: '24px',
                        background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                        border: 'none',
                        boxShadow: '0 10px 15px -3px rgba(24, 144, 255, 0.3)'
                    }}
                >
                    Create Your First Flow
                </Button>
            </div>
        </div>
    );
}
